/** Dealer back-office logins that leave the page and come back with an authorization code. */
export type CodeLoginProvider = 'KAKAO' | 'NAVER'

const STORAGE_KEYS: Record<CodeLoginProvider, string> = {
  KAKAO: 'dealer_kakao_login_state',
  NAVER: 'dealer_naver_login_state',
}
const CALLBACK_PATHS: Record<CodeLoginProvider, string> = {
  KAKAO: '/auth/kakao/callback',
  NAVER: '/auth/naver/callback',
}
const LOGIN_STATE_TTL_MS = 600_000 // 10 minutes

/** Callback page of a code login — must match the provider console exactly. */
export function buildOAuthRedirectUri(provider: CodeLoginProvider): string {
  return `${window.location.origin}${CALLBACK_PATHS[provider]}`
}

/**
 * Starts a code login: a fresh CSRF `state` kept in this tab for 10 minutes and sent to the
 * provider. The callback accepts the code only when the same state comes back (개발항목 166).
 */
export function startOAuthLoginState(provider: CodeLoginProvider): string {
  const state = crypto.randomUUID()
  try {
    sessionStorage.setItem(STORAGE_KEYS[provider], JSON.stringify({ state, timestamp: Date.now() }))
  } catch {
    // sessionStorage may be unavailable (private mode) — the callback then refuses the code
  }
  return state
}

/** Reads and forgets the state saved when the login started (null when none is live). */
export function takeOAuthLoginState(provider: CodeLoginProvider): string | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEYS[provider])
    sessionStorage.removeItem(STORAGE_KEYS[provider])
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    if (!('state' in parsed) || !('timestamp' in parsed)) return null
    const { state, timestamp } = parsed
    if (typeof state !== 'string' || state === '' || typeof timestamp !== 'number') return null
    return Date.now() - timestamp > LOGIN_STATE_TTL_MS ? null : state
  } catch {
    return null
  }
}
